import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { ArrowRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGrid, FyndanuGridSkeleton, fyndanuListingsQuery } from "@/components/FyndanuGrid";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { FiNav, FiSwedishLink, hreflangPair } from "@/components/FiNav";
import { fiAutotFaq, fiAutotSections } from "@/data/fi-content";

const TITLE = "Asuntoautot myynnissä Ruotsissa – Kalix, 90 km Torniosta";
const DESC =
  "Käytetyt asuntoautot Kalixissa. Kosteusmitattu ja kaasutarkastettu jokainen auto. Palvelemme suomeksi ja neuvomme tuonnissa Suomeen.";
const URL = "https://www.akhusbilfritid.se/fi/asuntoautot";

export const Route = createFileRoute("/fi/asuntoautot")({
  codeSplitGroupings: [["loader", "component"]],
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(fyndanuListingsQuery);
    return { faqLd: faqSchema(fiAutotFaq) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Asuntoautot myynnissä Kalixissa" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangPair("/fi/asuntoautot", "/husbilar-till-salu")],
    scripts: loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : [],
  }),
  component: FiAutotPage,
});

function FiAutotPage() {
  return (
    <>
      <PageHeader
        label="Asuntoautot · Kosteusmitattu ja kaasutarkastettu"
        title={
          <>
            Asuntoautot <span className="font-italic-display text-accent">myynnissä</span>.
          </>
        }
        sub="Koko varastomme päivittyy tälle sivulle automaattisesti. Jokainen auto on kosteusmitattu ja kaasulaitteiltaan tarkastettu luovutuksessa. Soita niin käydään auto läpi suomeksi."
      />

      <FiNav />

      <section className="container-edge py-12">
        <p className="eyebrow">Varasto</p>
        <Suspense fallback={<FyndanuGridSkeleton />}>
          <FyndanuGrid />
        </Suspense>
        <div className="mt-8">
          <Link
            to="/fi/yhteystiedot"
            className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Kysy autosta suomeksi <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <HubEditorial
        eyebrow="Ostajan opas"
        sections={fiAutotSections}
        faq={fiAutotFaq}
        faqTitle="Usein kysytyt kysymykset"
      />

      <FiSwedishLink to="/husbilar-till-salu" label="Husbilar till salu (svenska)" />
    </>
  );
}
